// ManiDine Restaurant Web — src/pages/reports/StoreReports.jsx — Updated: 2026-03-31 13:00 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportChart,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "7d", start: "", end: "" };

// ---------- helpers ----------

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number(value || 0));

const getOrderDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getOrderTimelineDate = (order) =>
  getOrderDate(
    order?.createdAt || order?.placedAt || order?.orderTime || order?.updatedAt
  );

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const parseNumber = (v, fallback = 0) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

const normalizeStatus = (status) => {
  if (!status) return "";
  return status.toString().toLowerCase();
};

const isSalesEligibleStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return true;
  if (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  ) {
    return false;
  }
  return (
    s.includes("complete") ||
    s.includes("deliver") ||
    s.includes("finish") ||
    s.includes("paid") ||
    s.includes("accept") ||
    s.includes("on_the_way")
  );
};

const isCompletedStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return false;
  if (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void")
  ) {
    return false;
  }
  return (
    s.includes("complete") ||
    s.includes("deliver") ||
    s.includes("finish") ||
    s.includes("paid")
  );
};

const isCancelledStatus = (status) => {
  const s = normalizeStatus(status);
  if (!s) return false;
  return (
    s.includes("cancel") ||
    s.includes("reject") ||
    s.includes("refun") ||
    s.includes("void") ||
    s.includes("declin")
  );
};

const getOrderTax = (order) => {
  if (typeof order?.tax === "number") return order.tax;
  if (typeof order?.taxAmount === "number") return order.taxAmount;
  return 0;
};

const getOrderTip = (order) => {
  if (typeof order?.tip === "number") return order.tip;
  if (typeof order?.tipAmount === "number") return order.tipAmount;
  return 0;
};

const getOrderDeliveryFee = (order) => {
  if (typeof order?.deliveryFee === "number") return order.deliveryFee;
  if (typeof order?.deliveryCharge === "number") return order.deliveryCharge;
  if (typeof order?.shippingFee === "number") return order.shippingFee;
  return 0;
};

const getItemsFoodSubtotal = (order) =>
  (order?.items || []).reduce((sum, item) => {
    const price = parseNumber(item?.price);
    const quantity = parseNumber(item?.quantity ?? 1, 1);
    return sum + price * quantity;
  }, 0);

const getRestaurantFoodSubtotal = (order) => {
  if (typeof order?.subtotal === "number") return order.subtotal;
  if (typeof order?.itemsSubtotal === "number") return order.itemsSubtotal;
  if (typeof order?.foodSubtotal === "number") return order.foodSubtotal;

  const itemsSubtotal = getItemsFoodSubtotal(order);
  if (itemsSubtotal > 0) return itemsSubtotal;

  if (typeof order?.totalAmount === "number" || typeof order?.total === "number") {
    const total = parseNumber(order?.totalAmount ?? order?.total);
    const tax = getOrderTax(order);
    const deliveryFee = getOrderDeliveryFee(order);
    const tip = getOrderTip(order);
    return Math.max(total - tax - deliveryFee - tip, 0);
  }

  return 0;
};

// ---------- component ----------

const StoreReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [ordersDocs, setOrdersDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        if (!restaurantId) {
          console.warn("[StoreReports] No restaurantId yet, skipping fetch");
          setOrdersDocs([]);
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );

        const snap = await getDocs(ordersQuery);

        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        console.log("[StoreReports] loaded orders:", rows.length);
        if (rows[0]) {
          console.log("[StoreReports] sample order:", rows[0]);
        }

        setOrdersDocs(rows);
      } catch (err) {
        console.error("[StoreReports] Load orders failed:", err);
        setOrdersDocs([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  const filteredOrders = useMemo(() => {
    if (!ordersDocs.length) return [];

    const { start, end } = resolveDateRange(dateRange);

    return ordersDocs
      .filter((order) => {
        const d = getOrderTimelineDate(order);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      })
      .map((order) => {
        const status = order.status || "pending";
        const date = getOrderTimelineDate(order);
        const foodSubtotal = parseNumber(getRestaurantFoodSubtotal(order));
        const tax = parseNumber(getOrderTax(order));
        const restaurantSales = isSalesEligibleStatus(status)
          ? foodSubtotal + tax
          : 0;

        return {
          ...order,
          __status: status,
          __date: date,
          __period: date ? date.toISOString().slice(0, 10) : "unknown",
          __foodSubtotal: foodSubtotal,
          __tax: tax,
          __restaurantSales: restaurantSales,
        };
      });
  }, [ordersDocs, dateRange]);

  const kpis = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const completedOrders = filteredOrders.filter((o) =>
      isCompletedStatus(o.__status)
    ).length;
    const cancelledOrders = filteredOrders.filter((o) =>
      isCancelledStatus(o.__status)
    ).length;
    const restaurantSales = filteredOrders.reduce(
      (sum, order) => sum + order.__restaurantSales,
      0
    );

    const uniquePeriods = new Set(
      filteredOrders
        .map((o) => o.__period)
        .filter((p) => p && p !== "unknown")
    );
    const openDays = uniquePeriods.size;

    const { start, end } = resolveDateRange(dateRange);
    let daysInRange = openDays;
    if (start && end) {
      const msPerDay = 1000 * 60 * 60 * 24;
      daysInRange = Math.max(
        1,
        Math.round((end.getTime() - start.getTime()) / msPerDay)
      );
    }

    const inactiveDays = Math.max(daysInRange - openDays, 0);
    const avgOrdersPerOpenDay = openDays ? totalOrders / openDays : 0;
    const cancelRate = totalOrders ? (cancelledOrders / totalOrders) * 100 : 0;
    const completionRate = totalOrders
      ? (completedOrders / totalOrders) * 100
      : 0;

    return {
      totalOrders,
      completedOrders,
      cancelledOrders,
      openDays,
      inactiveDays,
      avgOrdersPerOpenDay,
      cancelRate,
      completionRate,
      restaurantSales,
    };
  }, [filteredOrders, dateRange]);

  const dailySummary = useMemo(() => {
    const map = new Map();

    filteredOrders.forEach((order) => {
      const key = order.__period;
      const current = map.get(key) || {
        id: key,
        period: key,
        totalOrders: 0,
        completed: 0,
        cancelled: 0,
        restaurantSales: 0,
      };

      const completed = isCompletedStatus(order.__status);
      const cancelled = isCancelledStatus(order.__status);

      map.set(key, {
        ...current,
        totalOrders: current.totalOrders + 1,
        completed: current.completed + (completed ? 1 : 0),
        cancelled: current.cancelled + (cancelled ? 1 : 0),
        restaurantSales: current.restaurantSales + order.__restaurantSales,
      });
    });

    return [...map.values()].sort((a, b) => a.period.localeCompare(b.period));
  }, [filteredOrders]);

  const statusSummary = useMemo(() => {
    const map = new Map();

    filteredOrders.forEach((order) => {
      const key = normalizeStatus(order.__status) || "unknown";
      const human =
        isCompletedStatus(key)
          ? "Completed"
          : isCancelledStatus(key)
          ? "Cancelled / rejected"
          : key.includes("progress") || key.includes("ready")
          ? "In progress"
          : key || "Unknown";

      const current = map.get(key) || {
        id: key,
        status: human,
        count: 0,
      };

      map.set(key, { ...current, count: current.count + 1 });
    });

    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [filteredOrders]);

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      <section className="reports-kpi-grid">
        <ReportCard title="Total orders" value={kpis.totalOrders} />
        <ReportCard
          title="Completed orders"
          value={kpis.completedOrders}
          subtitle={`${kpis.completionRate.toFixed(1)}% completion`}
        />
        <ReportCard
          title="Cancelled / rejected"
          value={kpis.cancelledOrders}
          subtitle={`${kpis.cancelRate.toFixed(1)}% of orders`}
        />
        <ReportCard
          title="Restaurant sales"
          value={formatCurrency(kpis.restaurantSales)}
          subtitle="Food subtotal + tax only"
        />
        <ReportCard
          title="Open days"
          value={kpis.openDays}
          subtitle={`${kpis.inactiveDays} inactive days`}
        />
        <ReportCard
          title="Avg orders per active day"
          value={kpis.avgOrdersPerOpenDay.toFixed(1)}
        />
      </section>

      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 14,
          background: "rgba(15, 23, 42, 0.04)",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        Store sales shown below use only the restaurant portion of each order:
        <strong> food subtotal + tax</strong>. Delivery fees and tips are not
        included in these totals.
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Orders over time</h3>
          <ReportChart
            type="bar"
            data={dailySummary}
            xKey="period"
            series={[
              { dataKey: "totalOrders", name: "Total orders" },
              { dataKey: "completed", name: "Completed" },
            ]}
          />
        </div>
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Daily performance</h3>
          <ReportTable
            columns={[
              { title: "Period", dataIndex: "period", key: "period" },
              { title: "Orders", dataIndex: "totalOrders", key: "totalOrders" },
              { title: "Completed", dataIndex: "completed", key: "completed" },
              { title: "Cancelled", dataIndex: "cancelled", key: "cancelled" },
              {
                title: "Restaurant sales",
                dataIndex: "restaurantSales",
                key: "restaurantSales",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={dailySummary}
          />
        </div>

        <div className="reports-column">
          <h3 className="reports-section-title">Status breakdown</h3>
          <ReportTable
            columns={[
              { title: "Status", dataIndex: "status", key: "status" },
              { title: "Orders", dataIndex: "count", key: "count" },
            ]}
            data={statusSummary}
          />
        </div>
      </section>

      {!loading && filteredOrders.length === 0 && (
        <p className="reports-empty">
          No orders in this period for your store.
        </p>
      )}

      {loading && <div className="reports-loading">Loading data…</div>}
    </div>
  );
};

export default StoreReports;
